import React, { useEffect, useId, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '../../services/authService';
import { columnOptionLabels } from './criteriaUtils';

function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && e.msg != null ? e.msg : JSON.stringify(e))).join('\n');
  }
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
}

/**
 * Free-text column search (bypasses folder navigation).
 * Calls `/column-typeahead` and invokes `onSelect` with column + folder_list.
 *
 * `searchParams` are merged into the typeahead request (e.g. visible, folder_path, folder_null).
 * If `searchParams` does not define `visible`, defaults to `{ visible: true }` (segment builder).
 * Pass `visible: null` to search any visibility (Data Dictionary).
 * `onClear` is called when the input is emptied (so parents can drop a prior selection).
 */
const ColumnSearchBar = ({ onSelect, onClear = null, selectedLabel = '', searchParams = null }) => {
  const reactId = useId();
  const listboxId = `${reactId}-listbox`;
  const optionId = (index) => `${reactId}-option-${index}`;
  const [query, setQuery] = useState(selectedLabel || '');
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const searchParamsRef = useRef(searchParams);
  const requestIdRef = useRef(0);
  searchParamsRef.current = searchParams;

  useEffect(() => {
    setQuery(selectedLabel || '');
    setHits([]);
    setSearchError('');
    setOpen(false);
    setLoading(false);
    setActiveIndex(-1);
    clearTimeout(timerRef.current);
    requestIdRef.current += 1;
  }, [selectedLabel]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-option-index="${activeIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const runSearch = (q) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const trimmed = q.trim();
      if (!trimmed) {
        requestIdRef.current += 1;
        setHits([]);
        setSearchError('');
        setOpen(false);
        setLoading(false);
        setActiveIndex(-1);
        return;
      }
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setActiveIndex(-1);
      try {
        const extra = searchParamsRef.current;
        const params = {
          q: trimmed,
          size: 50,
          ...(extra && typeof extra === 'object' ? extra : {}),
        };
        // Default visible=true for segment builder. Data Dictionary passes `visible` explicitly
        // (including null = any) so non-visible columns can be found and edited.
        if (!extra || !Object.prototype.hasOwnProperty.call(extra, 'visible')) {
          params.visible = true;
        }
        Object.keys(params).forEach((key) => {
          if (params[key] == null) delete params[key];
        });
        const res = await api.get('/api/v2/segments/column-typeahead', { params });
        if (requestId !== requestIdRef.current) return;
        const nextHits = Array.isArray(res.data?.hits) ? res.data.hits : [];
        setHits(nextHits);
        setSearchError(res.data?.error || '');
        setOpen(true);
        setActiveIndex(nextHits.length > 0 && !res.data?.error ? 0 : -1);
      } catch (e) {
        if (requestId !== requestIdRef.current) return;
        setHits([]);
        setSearchError(formatApiErrorDetail(e.response?.data?.detail) || 'Column search failed');
        setOpen(true);
        setActiveIndex(-1);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, 200);
  };

  const handlePick = (hit) => {
    const label = hit.display_name || hit.column_name || hit.column || '';
    onSelect({
      catalog: hit.catalog || '',
      schema: hit.schema || '',
      table: hit.table || '',
      column: hit.column || '',
      display_name: label,
      column_name: label,
      column_type: hit.column_type || '',
      has_value_list: Boolean(hit.has_value_list),
      folder_list: Array.isArray(hit.folder_list) ? hit.folder_list : [],
    });
    setQuery(label);
    setOpen(false);
    setHits([]);
    setActiveIndex(-1);
  };

  const clearInput = () => {
    setQuery('');
    if (typeof onClear === 'function') onClear();
    runSearch('');
  };

  const showResults = open && !loading && !searchError && hits.length > 0;
  const activeOptionId = activeIndex >= 0 ? optionId(activeIndex) : undefined;
  const showClear = Boolean(query);
  const hitLabels = columnOptionLabels(hits);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
        setActiveIndex(-1);
      }
      return;
    }

    if (e.key !== 'Enter' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    if (!open || loading || searchError || hits.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < hits.length - 1 ? prev + 1 : 0));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : hits.length - 1));
      return;
    }

    // Enter: pick highlighted suggestion, or the first result if none highlighted.
    e.preventDefault();
    e.stopPropagation();
    const index = activeIndex >= 0 && activeIndex < hits.length ? activeIndex : 0;
    handlePick(hits[index]);
  };

  return (
    <div className="criteria-ta-wrap criteria-column-search" ref={wrapRef}>
      <div className="criteria-search-field">
        <input
          type="text"
          className={`criteria-search-input${showClear ? ' has-clear' : ''}`}
          placeholder="Search columns (e.g. birth year)…"
          value={query}
          autoComplete="off"
          role="combobox"
          aria-label="Search columns"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeOptionId}
          onChange={(e) => {
            const next = e.target.value;
            setQuery(next);
            if (!next.trim()) {
              if (typeof onClear === 'function') onClear();
            }
            runSearch(next);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (hits.length || searchError) setOpen(true);
          }}
        />
        {showClear && (
          <button
            type="button"
            className="criteria-search-clear"
            aria-label="Clear search"
            onClick={clearInput}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
      {open && (
        <ul
          id={listboxId}
          className="criteria-ta-results"
          role="listbox"
          ref={listRef}
        >
          {loading && (
            <li>
              <button type="button" disabled>
                Searching…
              </button>
            </li>
          )}
          {!loading && searchError && (
            <li>
              <button type="button" disabled>
                {searchError}
              </button>
            </li>
          )}
          {!loading && !searchError && hits.length === 0 && (
            <li>
              <button type="button" disabled>
                No columns found
              </button>
            </li>
          )}
          {showResults &&
            hits.map((h, index) => {
              const label = hitLabels[index];
              return (
              <li key={h._id} role="presentation">
                <button
                  type="button"
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === activeIndex}
                  data-option-index={index}
                  className={index === activeIndex ? 'is-active' : undefined}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => handlePick(h)}
                >
                  {label}
                </button>
              </li>
              );
            })}
        </ul>
      )}
    </div>
  );
};

export default ColumnSearchBar;
